import { Link } from "react-router-dom";
import { ArrowLeft, Phone, Mail, User, Asterisk } from "lucide-react";

const OWNER = {
  name: "Madan",
  phone: "+91 00000 00000",
  phoneRaw: "+910000000000",
  email: "hello@vogueverve.com",
};

const SECTIONS = [
  {
    id: "about",
    title: "01. About this platform",
    body: [
      `VOGUEVERVE is a private demonstration platform built and operated exclusively on the instructions of ${OWNER.name}. All product decisions, editorial direction, brand voice, feature scope and release timing are controlled by ${OWNER.name}.`,
      "The builders and engineers implementing this platform act only as executors of those instructions and hold no independent authority over its content, use, or commercial deployment.",
      "The platform is a work-in-progress modeling agency portal. Nothing on this site should be treated as a live, signed commercial service without written confirmation from the owner named above.",
    ],
  },
  {
    id: "terms",
    title: "02. Terms of use",
    body: [
      "By accessing or submitting an application through this website, you acknowledge that any information you provide is submitted voluntarily and may be reviewed by the owner or his appointed team for scouting purposes only.",
      "You retain ownership of all photographs, videos and personal details you submit. Submitting an application does not create any agency-model contract, retainer, or guarantee of representation. A separate signed contract is required before any professional engagement.",
      "You warrant that any content you upload is either your own or you have full permission to share it. You must be 18 years of age or older to submit an application. Applicants under 18 must have written consent from a parent or legal guardian.",
      "The owner reserves the right to accept, decline, delete, or archive any application at his sole discretion, at any time, without notice or explanation.",
    ],
  },
  {
    id: "privacy",
    title: "03. Privacy",
    body: [
      "We collect only the information you voluntarily provide in the Get Scouted form: name, email, phone, age, location, measurements, social handles, portfolio link, career goals, and up to four photographs.",
      "This information is stored solely for evaluation by the owner and his appointed scouting team. It is not sold, rented, or shared with any third-party advertiser.",
      `You may request access, correction, or full deletion of your submitted data at any time by writing to ${OWNER.email} or by calling the owner directly on ${OWNER.phone}.`,
      "Cookies used on this site are limited to essential session storage (e.g. remembering that you have dismissed the mobile splash). No behavioural advertising cookies are set.",
    ],
  },
  {
    id: "editorial",
    title: "04. Editorial references & third-party links",
    body: [
      "This site contains outbound links to Vogue.com and other Condé Nast properties for illustrative and reference purposes only. VOGUEVERVE is not affiliated with, endorsed by, or a partner of Condé Nast, Vogue, or any of its regional editions.",
      "All trademarks, logos and article references belong to their respective owners. If you own any linked content and would like it removed, please contact the owner using the details below.",
    ],
  },
  {
    id: "liability",
    title: "05. Limitation of liability",
    body: [
      "The platform is provided on an “as-is” basis. To the maximum extent permitted by law, neither the owner nor the builders shall be liable for any indirect, incidental, or consequential loss arising from use of this site.",
      "Casting outcomes, timelines, and commercial success are inherently subjective. No guarantee, promise, or representation is made about earnings, bookings, or career results.",
    ],
  },
];

export default function Legal() {
  return (
    <main data-testid="legal-page" className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-8 flex items-center justify-between gap-4">
          <Link
            to="/"
            data-testid="legal-back-home"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-white/60 hover:text-white transition-colors"
          >
            <ArrowLeft size={14} /> Back home
          </Link>
          <span className="font-display text-lg font-black tracking-tighter uppercase">
            Vogue<span className="text-white/70">Verve</span>
          </span>
        </div>
      </header>

      {/* Title */}
      <section className="max-w-3xl mx-auto px-6 md:px-10 pt-20 pb-14">
        <p className="text-[10px] uppercase tracking-[0.35em] font-bold text-white/40 mb-6">
          The fine print
        </p>
        <h1 className="font-display text-5xl md:text-7xl font-light tracking-tight leading-[0.95] mb-6">
          Terms, privacy & <span className="italic">every&shy;thing</span> in between.
        </h1>
        <p className="text-base text-white/60 leading-relaxed max-w-xl">
          A short, honest set of rules for using VOGUEVERVE. Written in plain language so you always know exactly where you stand.
        </p>
      </section>

      {/* Starred note — key disclosure */}
      <section className="max-w-3xl mx-auto px-6 md:px-10 pb-16">
        <div
          data-testid="owner-note"
          className="relative border border-white/25 rounded-2xl p-8 bg-white/[0.03]"
        >
          <div className="absolute -top-4 left-6 flex items-center gap-1 bg-[#0a0a0a] px-3 py-1 rounded-full border border-white/25">
            <Asterisk size={12} strokeWidth={2.4} />
            <span className="text-[10px] uppercase tracking-[0.3em] font-bold">Important</span>
          </div>
          <p className="text-sm md:text-base text-white/85 leading-relaxed">
            <strong className="text-white">VOGUEVERVE is a platform being built exclusively on the instructions of {OWNER.name}.</strong> All legal, editorial, and commercial matters — including questions about your data, applications, or any dispute — are handled directly by him. The builders and engineers behind this site act solely on his direction.
          </p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex items-center gap-3">
              <User size={16} className="text-white/50" />
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40">Owner</p>
                <p className="text-sm text-white">{OWNER.name}</p>
              </div>
            </div>
            <a
              href={`tel:${OWNER.phoneRaw}`}
              data-testid="owner-phone"
              className="flex items-center gap-3 hover:opacity-80 transition-opacity"
            >
              <Phone size={16} className="text-white/50" />
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40">Direct line</p>
                <p className="text-sm text-white">{OWNER.phone}</p>
              </div>
            </a>
            <a
              href={`mailto:${OWNER.email}`}
              data-testid="owner-email"
              className="flex items-center gap-3 hover:opacity-80 transition-opacity"
            >
              <Mail size={16} className="text-white/50" />
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40">Email</p>
                <p className="text-sm text-white break-all">{OWNER.email}</p>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Sections */}
      <section className="max-w-3xl mx-auto px-6 md:px-10 pb-24 space-y-14">
        {SECTIONS.map((s) => (
          <article key={s.id} id={s.id} data-testid={`legal-section-${s.id}`} className="scroll-mt-24">
            <h2 className="font-display text-2xl md:text-3xl font-light tracking-tight mb-4">
              {s.title}
            </h2>
            <div className="space-y-4">
              {s.body.map((p, i) => (
                <p key={i} className="text-sm md:text-base text-white/70 leading-relaxed">
                  {p}
                </p>
              ))}
            </div>
          </article>
        ))}

        {/* Sign-off */}
        <div className="pt-10 border-t border-white/10">
          <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/40 mb-3">
            All questions
          </p>
          <p className="font-script text-white text-4xl md:text-5xl leading-none mb-6">
            reach {OWNER.name.split(" ")[0].toLowerCase()} directly.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href={`tel:${OWNER.phoneRaw}`}
              data-testid="legal-cta-call"
              className="inline-flex items-center gap-3 rounded-full bg-white text-black px-7 py-4 text-xs uppercase tracking-[0.25em] font-bold hover:bg-white/80 transition-colors"
            >
              <Phone size={14} strokeWidth={2.4} /> Call {OWNER.phone}
            </a>
            <a
              href={`mailto:${OWNER.email}`}
              data-testid="legal-cta-email"
              className="inline-flex items-center gap-3 rounded-full bg-transparent border border-white/25 text-white px-7 py-4 text-xs uppercase tracking-[0.25em] font-bold hover:border-white transition-colors"
            >
              <Mail size={14} strokeWidth={2.4} /> {OWNER.email}
            </a>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 py-8">
        <p className="max-w-3xl mx-auto px-6 md:px-10 text-[10px] uppercase tracking-[0.25em] text-white/30">
          &copy; {new Date().getFullYear()} VOGUEVERVE · Operated on the instructions of {OWNER.name}.
        </p>
      </footer>
    </main>
  );
}
